import type { ColumnDef } from '@tanstack/react-table';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Icon } from '~/components';
import { DataUnavailable } from '~/components/DataUnavailable';
import { STICKY_TABLE_HEADER_CLASS } from '~/components/ScrollCollapse/constants/classes';
import { cn } from '~/lib/cn';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '~/modules/artist-roster/components/Table';
import { SkeletonTable } from '~/modules/artist-roster/components/Table/components/SkeletonTable';
import { TableHeaderBackground } from '~/modules/artist-roster/components/Table/components/TableHeaderBackground';
import type {
  ArtistTableHeaderMetaType,
  ArtistTableMetaType,
  ArtistType,
} from '~/modules/artist-roster/types';
import { useAnalytics } from '~/modules/monitoring/analytics/useAnalytics';

interface ArtistsTableProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: ColumnDef<ArtistType, any>[];
  data: ArtistType[];
  isLoading: boolean;
  meta?: ArtistTableMetaType;
  className?: string;
}

export function ArtistsTable({
  columns,
  data,
  meta,
  isLoading,
  className,
}: ArtistsTableProps) {
  const { t } = useTranslation('roster');
  const { trackEvent } = useAnalytics('Artist Roster');
  const table = useReactTable({
    data,
    columns,
    meta,
    getCoreRowModel: getCoreRowModel(),
  });

  const handleArtistLinkClick = ({
    artistData,
  }: {
    artistData: ArtistType;
  }) => {
    const tableMeta = table.options?.meta as ArtistTableMetaType;
    const {
      filtersCount = 0,
      crossoverFilterApplied = false,
      filtersApplied = false,
      rosterStatus = 'all',
    } = tableMeta || {};

    trackEvent('rosterTableArtistClick', {
      artist_id: artistData.partyId,
      artist_name: artistData.name,
      roster_label_filters_count: filtersCount,
      roster_crossover_filter_applied: crossoverFilterApplied,
      roster_label_filters_applied: filtersApplied,
      roster_status: rosterStatus,
    });
  };

  let body;

  if (isLoading) {
    body = (
      <SkeletonTable
        size={12}
        headers={table.getHeaderGroups()[0].headers}
        cellClassName="h-[5rem]"
      />
    );
  } else if (table.getRowModel().rows?.length) {
    body = table.getRowModel().rows.map((row, rowIndex) => (
      <TableRow
        aria-label={`Row for ${row.original?.name || 'artist'}`}
        key={row.id}
        data-state={row.getIsSelected() && 'selected'}
        className="group relative duration-300 ease-out hover:bg-emphasis-subtle"
      >
        {row.getVisibleCells().map((cell) => {
          let cellContent;

          if (cell.column.id === 'name') {
            if (row.original.partyId) {
              cellContent = (
                <Link
                  to={`/roster/${row.original.partyId}`}
                  className="group/link flex items-center gap-sm py-md pl-xl"
                  onClick={() =>
                    handleArtistLinkClick({ artistData: row.original })
                  }
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </Link>
              );
            } else {
              cellContent = (
                <div className="flex items-center gap-sm py-md pl-lg">
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </div>
              );
            }
          } else {
            cellContent = flexRender(
              cell.column.columnDef.cell,
              cell.getContext(),
            );
          }

          return (
            <TableCell
              key={cell.id}
              className={cn(
                'border-y p-0 border-transparent first:rounded-l-sm first:border-l last:rounded-r-sm last:border-r group-hover:border-emphasis-strong',
                rowIndex % 2 === 0 && 'bg-default-inverse/default',
              )}
            >
              {cellContent}
            </TableCell>
          );
        })}
      </TableRow>
    ));
  } else {
    body = (
      <TableRow>
        <TableCell colSpan={4}>
          <DataUnavailable
            title={t('noData.title')}
            subtitle={t('noData.sub')}
            icon={
              <Icon
                type="user-music"
                size="4x"
                variant="light"
                aria-hidden
                className="text-default"
              />
            }
          />
        </TableCell>
      </TableRow>
    );
  }

  return (
    <Table
      aria-label="Artists roster list"
      className={cn('w-full table-fixed px-xl pb-xl', className)}
    >
      <TableHeader className={STICKY_TABLE_HEADER_CLASS}>
        {table.getHeaderGroups().map((headerGroup) => (
          <TableRow key={headerGroup.id}>
            {headerGroup.headers.map((header, index) => {
              const headerMeta = header.column.columnDef
                .meta as ArtistTableHeaderMetaType;

              return (
                <TableHead
                  key={header.id}
                  className={cn(
                    'text-body-sm font-normal first:pl-lg',
                    headerMeta?.className,
                  )}
                >
                  {!index ? <TableHeaderBackground /> : null}
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                </TableHead>
              );
            })}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody>{body}</TableBody>
    </Table>
  );
}

ArtistsTable.displayName = 'ArtistsTable';
